import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, DownloadIcon, Loader2Icon, XIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { PhoneField } from '../form/PhoneField';
import { inputBase, inputBorder } from '../form/fieldStyles';
import { resourceBusinessTypes } from '../../data/guides';
import type { Resource } from '../../types/article';
type Errors = {
  name?: string;
  phone?: string;
  businessType?: string;
};
export function DownloadModal({
  resource,
  onClose
}: {
  resource: Resource | null;
  onClose: () => void;
}) {
  const reduce = useReducedMotion();
  return <AnimatePresence data-id="element-564" data-mp="3t7rbohga7ah0">
      {resource && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-565" data-mp="3t6g6cd76tuns">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="download-title" className="max-h-[92vh] w-full overflow-y-auto rounded-t-[18px] bg-white p-6 shadow-[0_24px_64px_-24px_rgba(11,11,11,0.45)] sm:max-w-[460px] sm:rounded-[16px] sm:p-8" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-566" data-mp="3rhbqf6ap2st5">
            <DownloadForm resource={resource} onClose={onClose} data-id="element-567" data-mp="n359n07mgbtz" />
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function DownloadForm({
  resource,
  onClose
}: {
  resource: Resource;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('RW');
  const [businessType, setBusinessType] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  const validate = (): Errors => {
    const e: Errors = {};
    if (name.trim().length < 2) e.name = 'Enter your name';
    const digits = phone.replace(/\D/g, '');
    if (country === 'RW' ? !/^(0?7\d{8})$/.test(digits) : digits.length < 7) e.phone = 'Enter a valid phone number';
    if (!businessType) e.businessType = 'Choose your business type';
    return e;
  };
  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      document.getElementById(`dl-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const download = () => {
    const blob = new Blob([`${resource.title}\n\nSample file from Kayko. The final ${resource.format} template is being prepared.\n`], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kayko-${resource.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const close = <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas-warm hover:text-ink" data-id="element-568" data-mp="q6xqg6lfrrq2">
      <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-569" data-mp="9yhpnwkgb13k" />
    </button>;
  if (status === 'success') {
    return <div className="relative text-center" data-id="element-570" data-mp="3s54jzxe1wyhw">
        {close}
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E6F4EA] text-[#1B7A3D]" data-id="element-571" data-mp="3s6panbx8vxah">
          <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-572" data-mp="onvzt6vupmk0" />
        </span>
        <h2 id="download-title" className="display mt-5 text-[26px] font-semibold text-ink" data-id="element-573" data-mp="oq077ysyl3p1">
          Your download is ready
        </h2>
        <p className="mt-2 text-[15px] leading-[1.55] text-ink-soft" data-id="element-574" data-mp="eymafyse6hzl">
          {resource.title} · {resource.format} · {resource.size}
        </p>
        <button type="button" onClick={download} className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-575" data-mp="jut5gwj9292r">
          <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-576" data-mp="1lq7f6xau1gtm" />
          Download {resource.format} file
        </button>
        <p className="mt-5 text-[14px] text-ink-muted" data-id="element-577" data-mp="1vhtwnbdrkc4t">
          Tired of spreadsheets?{' '}
          <SmartLink href="/get-started" className="font-semibold text-brand hover:text-brand-hover" data-id="element-578" data-mp="1vjo5vsfoaft2">
            Try Kayko instead
          </SmartLink>
        </p>
      </div>;
  }
  const labelCls = 'mb-1.5 block text-[14px] font-medium text-ink';
  const errCls = 'mt-1.5 text-[13px] font-medium text-[#C0362C]';
  return <form onSubmit={submit} noValidate className="relative" data-id="element-579" data-mp="1crvfm5rzn8p1">
      {close}
      <p className="text-[13px] font-semibold text-brand" data-id="element-580" data-mp="17s8znokrzecj">Free {resource.type.toLowerCase()}</p>
      <h2 id="download-title" className="mt-1 pr-10 text-[22px] font-semibold leading-[1.3] tracking-[-0.02em] text-ink" data-id="element-581" data-mp="2o6ioskwud74f">
        {resource.title}
      </h2>
      <p className="mt-2 text-[14px] text-ink-muted" data-id="element-582" data-mp="2j6mmqr7pfpri">Tell us a little about you and we’ll send it straight away.</p>

      <div className="mt-6 space-y-4" data-id="element-583" data-mp="2r7u9sg96xu4a">
        <div data-id="element-584" data-mp="2v9fj5rminq9q">
          <label htmlFor="dl-name" className={labelCls} data-id="element-585" data-mp="2v8xlqro4b608">
            Full name <span className="text-brand" data-id="element-586" data-mp="308sdsyjmq1mv">*</span>
          </label>
          <input ref={firstRef} id="dl-name" autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Aline Uwimana" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'dl-name-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.name))}`} data-id="element-587" data-mp="309lcdc8fpktx" />
          {errors.name && <p id="dl-name-err" className={errCls} data-id="element-588" data-mp="2m8r4qvt7nnwb">{errors.name}</p>}
        </div>
        <div data-id="element-589" data-mp="1otmxgrqks4ks">
          <label htmlFor="dl-phone" className={labelCls} data-id="element-590" data-mp="1otwmod3sq7pf">
            Phone number <span className="text-brand" data-id="element-591" data-mp="1orit68orl3jz">*</span>
          </label>
          <PhoneField id="dl-phone" value={phone} country={country} hasError={Boolean(errors.phone)} required describedBy={errors.phone ? 'dl-phone-err' : undefined} onChange={setPhone} onCountryChange={setCountry} onBlur={() => undefined} data-id="element-592" data-mp="1treycb977114" />
          {errors.phone && <p id="dl-phone-err" className={errCls} data-id="element-593" data-mp="17cpqm0t2wk9v">{errors.phone}</p>}
        </div>
        <div data-id="element-594" data-mp="13a2ydyshwxc5">
          <label htmlFor="dl-businessType" className={labelCls} data-id="element-595" data-mp="13al0hc1ua5rz">
            Business type <span className="text-brand" data-id="element-596" data-mp="yjw8lvm8z6sb">*</span>
          </label>
          <div className="relative" data-id="element-597" data-mp="yhjiuwa8zs4p">
            <select id="dl-businessType" value={businessType} onChange={e => setBusinessType(e.target.value)} aria-invalid={Boolean(errors.businessType)} aria-describedby={errors.businessType ? 'dl-bt-err' : undefined} className={`${inputBase} ${inputBorder(Boolean(errors.businessType))} appearance-none pr-10 ${businessType ? '' : 'text-ink-muted/70'}`} data-id="element-598" data-mp="tk05dyldjjdt">
              <option value="" disabled data-id="element-599" data-mp="23i839ztrywfx">
                Choose one
              </option>
              {resourceBusinessTypes.map(t => <option key={t} value={t} data-id="element-600" data-mp="1tjs73ifw4wzk">
                  {t}
                </option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-4 h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-601" data-mp="1yhbtx6qnmi34" />
          </div>
          {errors.businessType && <p id="dl-bt-err" className={errCls} data-id="element-602" data-mp="1phf6f8g03p9n">{errors.businessType}</p>}
        </div>
      </div>

      <button type="submit" disabled={status === 'loading'} className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover disabled:opacity-80" data-id="element-603" data-mp="1at0zomfgrgnt">
        {status === 'loading' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-604" data-mp="15vsh4kqelam1" />
            Preparing your download…
          </> : 'Get my free download'}
      </button>
      <p className="mt-3 text-center text-[12px] text-ink-muted" data-id="element-605" data-mp="i8dtm1d4ubch">
        By submitting, you agree to our{' '}
        <SmartLink href="/legal/privacy" className="font-medium text-ink underline underline-offset-2 hover:text-brand" data-id="element-606" data-mp="w3poc8rag1te">
          Privacy Policy
        </SmartLink>
        .
      </p>
    </form>;
}